import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, ClipboardList, Users, Package,
  BarChart3, UserCog, LogOut, Menu, X, ChevronLeft, BookOpen, Settings, BoxIcon, Truck, HardHat, ShieldAlert
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Visao Geral", end: true, module: "dashboard" },
  { to: "/ordens", icon: ClipboardList, label: "Ordens de Producao", module: "ordens" },
  { to: "/produtos", icon: BoxIcon, label: "Produtos", module: "produtos" },
  { to: "/clientes", icon: Users, label: "Clientes", module: "clientes" },
  { to: "/estoque", icon: Package, label: "Estoque", module: "estoque" },
  { to: "/expedicao", icon: Truck, label: "Expedicao", module: "expedicao" },
  { to: "/engenharia", icon: HardHat, label: "Engenharia", module: "engenharia" },
  { to: "/relatorios", icon: BarChart3, label: "Relatorios", module: "relatorios" },
  { to: "/manual", icon: BookOpen, label: "Manual", module: "manual" },
  { to: "/auditoria", icon: ShieldAlert, label: "Auditoria", module: "auditoria" },
  { to: "/usuarios", icon: UserCog, label: "Usuarios", module: "usuarios" },
  { to: "/configuracoes", icon: Settings, label: "Configuracoes", module: "configuracoes" },
];

export default function Layout() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const allItems = navItems.filter((item) => can(item.module, "view"));

  const SidebarContent = () => (
    <nav className="flex flex-col gap-1 px-2" data-testid="sidebar-nav">
      {allItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={() => setSidebarOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-colors duration-150 ${
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-foreground/70 hover:bg-muted hover:text-foreground"
            }`
          }
          data-testid={`nav-${item.to.replace("/", "") || "dashboard"}`}
        >
          <item.icon className="h-[18px] w-[18px] shrink-0" />
          {!collapsed && <span>{item.label}</span>}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col border-r border-border bg-card transition-all duration-200 ${
          collapsed ? "w-16" : "w-60"
        }`}
        data-testid="sidebar"
      >
        <div className="h-14 flex items-center justify-between px-4 border-b border-border">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <img src="/logo-liga-facil.png" alt="Liga Facil" className="h-8 w-auto" />
            </div>
          )}
          {collapsed && (
            <img src="/logo-liga-facil.png" alt="Liga Facil" className="h-7 w-auto" />
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="h-8 w-8"
            data-testid="sidebar-collapse-btn"
          >
            <ChevronLeft className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
          </Button>
        </div>
        <div className="flex-1 py-4 overflow-y-auto">
          <SidebarContent />
        </div>
        <div className="p-3 border-t border-border">
          <div className={`flex items-center gap-2 ${collapsed ? "justify-center" : ""}`}>
            <div className="h-8 w-8 rounded bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium truncate">{user?.name}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider" data-testid="sidebar-user-role">{user?.role_name || user?.role}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-60 bg-card border-r border-border z-50 flex flex-col">
            <div className="h-14 flex items-center justify-between px-4 border-b border-border">
              <img src="/logo-liga-facil.png" alt="Liga Facil" className="h-8 w-auto" />
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)} data-testid="sidebar-close-btn">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 py-4 overflow-y-auto">
              <SidebarContent />
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="h-14 border-b border-border bg-card flex items-center justify-between px-4 lg:px-6" data-testid="top-header">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden h-8 w-8"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-4 w-4" />
            </Button>
            <span className="text-xs tracking-[0.1em] uppercase font-semibold text-muted-foreground hidden sm:block">
              Liga Facil - Planejamento e Controle de Producao
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                <div className="h-7 w-7 rounded bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <span className="text-sm hidden sm:block">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                {user?.username}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
